import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useMemo } from 'react';
import { Dialog, DialogContent, Box, IconButton, Typography, useTheme, useMediaQuery, Button, Divider, Slide } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { GetCommonSettings } from '@themes/common/data';
import { ImageSlider } from '@components/UI/ImageSlider/ImageSlider';
import { FormatMoney } from '@components/Shared/Utils/price';
/**
 * Product Quick View Modal Component
 *
 * Displays a product preview modal with images, pricing, and optional Shopify sections.
 *
 * Features:
 * - Responsive layout (mobile/desktop)
 * - Image gallery with zoom and fullscreen support
 * - Dynamic price display with custom offer text for zero-price products
 * - Shopify sections rendering (fetched via QUICK_VIEW_SECTION_IDS env var)
 * - Customizable call-to-action button text
 * - Slide-down transition animation
 * - Sticky footer with "View Product" button
 *
 * Props:
 * @param {boolean} open - Controls modal visibility
 * @param {function} onClose - Callback when modal is closed
 * @param {ProductData | null} product - Product data to display
 * @param {string} [offerText] - Custom text for zero-price products (default: "Offert")
 * @param {string} [buttonText] - Custom button text (defaults to translation)
 * @param {Record<string, string>} [sectionsHtml] - Shopify sections HTML to render before description
 *
 * @example
 * <ProductQuickViewModal
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   product={productData}
 *   offerText="Request Quote"
 *   buttonText="View Full Details"
 *   sectionsHtml={sectionsData}
 * />
 */
// Slide transition component
const SlideTransition = React.forwardRef(function Transition(props, ref) {
    return _jsx(Slide, Object.assign({ direction: 'down', ref: ref }, props));
});
const ProductQuickViewModal = ({ open, onClose, product, offerText, buttonText, sectionsHtml }) => {
    var _a;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { translations } = GetCommonSettings();
    // Convert product images to ImageSlideData format
    const images = useMemo(() => {
        var _a;
        if (!product)
            return [];
        const imgs = [];
        const seen = new Set();
        // Add product images
        if (product.images && Array.isArray(product.images) && product.images.length > 0) {
            product.images.forEach((src, index) => {
                if (!src || seen.has(src))
                    return;
                seen.add(src);
                imgs.push({
                    id: `${product.variant_id}-${index}`,
                    src,
                    alt: product.title,
                    thumbnail: src,
                    title: product.title,
                    width: 500,
                    height: 500,
                });
            });
        }
        // Fallback to featured media
        if (imgs.length === 0 && ((_a = product.featured_media) === null || _a === void 0 ? void 0 : _a.src) && !seen.has(product.featured_media.src)) {
            seen.add(product.featured_media.src);
            imgs.push({
                id: product.featured_media.id || `${product.variant_id}-featured`,
                src: product.featured_media.src,
                alt: product.title,
                thumbnail: product.featured_media.src,
                title: product.title,
                width: product.featured_media.width,
                height: product.featured_media.height,
            });
        }
        // Fallback to single image
        if (imgs.length === 0 && product.image && !seen.has(product.image)) {
            seen.add(product.image);
            imgs.push({
                id: `${product.variant_id}-image`,
                src: product.image,
                alt: product.title,
                thumbnail: product.image,
                title: product.title,
                width: 400,
                height: 400,
            });
        }
        // Fallback to placeholder
        if (imgs.length === 0) {
            imgs.push({
                id: 'placeholder',
                src: '/placeholder-image.png',
                alt: product.title,
                thumbnail: '/placeholder-image.png',
                title: product.title,
                width: 400,
                height: 400,
            });
        }
        return imgs;
    }, [product]);
    // Format pricing
    const priceDisplay = useMemo(() => {
        var _a, _b, _c, _d;
        if (!product)
            return { priceFormatted: '', compareAtPriceFormatted: '', unit: '', isOffer: false, hasComparePrice: false };
        const price = (_a = product.price) !== null && _a !== void 0 ? _a : 0;
        const compareAtPrice = (_b = product.compare_at_price) !== null && _b !== void 0 ? _b : 0;
        const unitFactor = (_c = product.unit_factor) !== null && _c !== void 0 ? _c : 1;
        const unit = (_d = product.unit) !== null && _d !== void 0 ? _d : '';
        // If price is 0, show as "Offert" (offer/quote)
        const isOffer = price === 0;
        if (isOffer) {
            // Use data attribute if provided, otherwise use hardcoded text
            return { priceFormatted: offerText || 'Offert', compareAtPriceFormatted: '', unit: '', isOffer: true, hasComparePrice: false };
        }
        const priceFormatted = FormatMoney(price * unitFactor);
        const compareAtPriceFormatted = compareAtPrice > price ? FormatMoney(compareAtPrice * unitFactor) : '';
        return {
            priceFormatted,
            compareAtPriceFormatted,
            unit,
            isOffer: false,
            hasComparePrice: compareAtPrice > price,
        };
    }, [product, offerText]);
    // Extract technical data from metafields or product properties
    const technicalData = useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        if (!product)
            return [];
        const data = [];
        // Material
        if (((_a = product.metafields) === null || _a === void 0 ? void 0 : _a.material) || ((_b = product.meta) === null || _b === void 0 ? void 0 : _b.material)) {
            data.push({
                label: 'Material',
                value: (_f = (_d = (_c = product.metafields) === null || _c === void 0 ? void 0 : _c.material) !== null && _d !== void 0 ? _d : (_e = product.meta) === null || _e === void 0 ? void 0 : _e.material) !== null && _f !== void 0 ? _f : '',
            });
        }
        // Color
        if (((_g = product.metafields) === null || _g === void 0 ? void 0 : _g.color) || ((_h = product.meta) === null || _h === void 0 ? void 0 : _h.color) || ((_j = product.variant_options) === null || _j === void 0 ? void 0 : _j.find((opt) => opt.toLowerCase().includes('color')))) {
            const colorValue = (_o = (_l = (_k = product.metafields) === null || _k === void 0 ? void 0 : _k.color) !== null && _l !== void 0 ? _l : (_m = product.meta) === null || _m === void 0 ? void 0 : _m.color) !== null && _o !== void 0 ? _o : (_p = product.variant_options) === null || _p === void 0 ? void 0 : _p.find((opt) => opt.toLowerCase().includes('color'));
            if (colorValue) {
                data.push({
                    label: 'Färg',
                    value: colorValue,
                });
            }
        }
        return data;
    }, [product]);
    // Product description
    const description = useMemo(() => {
        var _a;
        if (!product)
            return '';
        return (_a = product.description) !== null && _a !== void 0 ? _a : '';
    }, [product]);
    const handleContactClick = useCallback(() => {
        // Navigate to product page
        if (product === null || product === void 0 ? void 0 : product.url) {
            window.location.href = product.url;
        }
    }, [product === null || product === void 0 ? void 0 : product.url]);
    if (!product) {
        return null;
    }
    return (_jsxs(Dialog, Object.assign({ open: open, onClose: onClose, TransitionComponent: SlideTransition, maxWidth: 'lg', fullWidth: true, fullScreen: isMobile, PaperProps: {
            sx: {
                bgcolor: 'background.paper',
                maxHeight: isMobile ? '100vh' : '90vh',
            },
        } }, { children: [_jsx(Box, Object.assign({ sx: { position: 'absolute', top: 8, right: 8, zIndex: 1 } }, { children: _jsx(IconButton, Object.assign({ "aria-label": 'close', onClick: onClose, sx: { color: 'text.secondary' } }, { children: _jsx(CloseIcon, {}) })) })), _jsxs(DialogContent, Object.assign({ sx: { p: 0, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' } }, { children: [_jsxs(Box, Object.assign({ sx: { flex: 1, overflowY: 'auto', p: { xs: 1, md: 2 } } }, { children: [_jsxs(Box, Object.assign({ sx: { display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 3, mb: description ? 3 : 0 } }, { children: [_jsx(Box, Object.assign({ sx: { flex: isMobile ? '0 0 auto' : '1', minWidth: 0 } }, { children: _jsx(ImageSlider, { images: images, height: isMobile ? '40vh' : '60vh', showThumbnails: images.length > 1, showNavigation: images.length > 1, showPagination: images.length > 1, zoom: true, ariaLabel: `${product.title} product images`, priority: true, loading: 'eager', displayTitle: false }) })), _jsxs(Box, Object.assign({ sx: { flex: isMobile ? '0 0 auto' : '0 0 250px', display: 'flex', flexDirection: 'column', gap: 2 } }, { children: [_jsx(Typography, Object.assign({ variant: 'h5', component: 'h2', sx: { fontWeight: 600, fontSize: { xs: '1.25rem', md: '1.5rem' }, mt: { xs: 5, md: 6 } } }, { children: product.title })), _jsxs(Box, Object.assign({ className: 'price product__price' }, { children: [_jsxs(Box, Object.assign({ className: priceDisplay.hasComparePrice ? 'price__current price__current--on-sale' : 'price__current' }, { children: [priceDisplay.priceFormatted, !priceDisplay.isOffer && priceDisplay.unit && (_jsxs(Box, Object.assign({ component: 'span', className: 'sb-units' }, { children: [' ', "/ ", priceDisplay.unit] })))] })), priceDisplay.hasComparePrice && (_jsxs(Box, Object.assign({ className: 'price__compare-at visible' }, { children: [priceDisplay.compareAtPriceFormatted, _jsxs(Box, Object.assign({ component: 'span', className: 'sb-units', sx: { ml: 0.5 } }, { children: [' ', "/ ", priceDisplay.unit] }))] })))] })), technicalData.length > 0 && (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsxs(Box, { children: [_jsx(Typography, Object.assign({ variant: 'subtitle2', sx: { fontWeight: 600, mb: 1, textTransform: 'uppercase', fontSize: '0.875rem' } }, { children: "Technical data:" })), _jsx(Box, Object.assign({ sx: { display: 'flex', flexDirection: 'column', gap: 0.5 } }, { children: technicalData.map((item, index) => (_jsxs(Box, Object.assign({ sx: { display: 'flex', gap: 1 } }, { children: [_jsxs(Typography, Object.assign({ variant: 'body2', sx: { fontWeight: 600, minWidth: '80px' } }, { children: [item.label, ":"] })), _jsx(Typography, Object.assign({ variant: 'body2', sx: { color: 'text.secondary' } }, { children: item.value }))] }), index))) }))] })] }))] }))] })), sectionsHtml && Object.keys(sectionsHtml).length > 0 && (_jsx(Box, { children: Object.entries(sectionsHtml).map(([sectionId, html]) => (_jsx(Box, Object.assign({ "data-section-id": sectionId }, { children: _jsx(Typography, { component: 'div', dangerouslySetInnerHTML: { __html: html } }) }), sectionId))) })), description && (_jsx(Box, { children: _jsx(Typography, { component: 'div', dangerouslySetInnerHTML: { __html: description } }) }))] })), _jsx(Box, Object.assign({ sx: {
                            position: 'sticky',
                            bottom: 0,
                            bgcolor: 'background.paper',
                            borderTop: 1,
                            borderColor: 'divider',
                            p: { xs: 1, md: 2 },
                            boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
                            display: 'flex',
                            justifyContent: 'center',
                        } }, { children: _jsx(Button, Object.assign({ variant: 'contained', size: 'large', onClick: handleContactClick, sx: {
                                bgcolor: '#DA6A35',
                                color: 'white',
                                textTransform: 'uppercase',
                                fontWeight: 600,
                                py: 1.5,
                                px: 4,
                                '&:hover': {
                                    bgcolor: '#c55e2f',
                                },
                            } }, { children: buttonText || ((_a = translations.product_gallery) === null || _a === void 0 ? void 0 : _a.view_details) || 'Visa produkt' })) }))] }))] })));
};
export default ProductQuickViewModal;
